import { Timestamp } from '@angular/fire/firestore';
import { bookinginternal } from 'src/shared/services/client/client';

export interface StaffOnlineData {
  id: string;
  name: string;
  description: string;
  last_online: Timestamp;
  environment: string;
  photoUrl?: string;
  is_clinician?: boolean;
  is_developer?: boolean;
}

export function extractStaffOnlineRoleFlags(jwtRoles: string[] | undefined): {
  is_clinician: boolean;
  is_developer: boolean;
} {
  const allRoles = jwtRoles ?? [];

  return {
    is_clinician: allRoles.includes('clinician'),
    is_developer: allRoles.includes('developers'),
  };
}

export function buildStaffOnlineWritePayload(
  profile: Pick<bookinginternal.GetStaffProfileResponse, 'id' | 'name' | 'description' | 'photo_url'>,
  environmentName: string,
  jwtRoles: string[] | undefined,
  lastOnline: Date = new Date()
): StaffOnlineData {
  const roleFlags = extractStaffOnlineRoleFlags(jwtRoles);

  return {
    id: profile.id ?? '',
    name: profile.name ?? 'unknown',
    description: profile.description ?? 'unknown',
    last_online: Timestamp.fromDate(lastOnline),
    environment: environmentName,
    photoUrl: profile.photo_url ?? '',
    is_clinician: roleFlags.is_clinician,
    is_developer: roleFlags.is_developer,
  };
}

/** Max gap between shift end and next start to treat as one continuing period. */
export const SHIFT_MERGE_GAP_MS = 15 * 60 * 1000;

export interface StaffRotaListItem {
  staffProfileId: string;
  name: string;
  displayName: string;
  photoUrl?: string;
  licensedStates: string[];
  secondaryLine: string;
  isOnline: boolean;
  /** Online with no covering shift, but a recently ended shift is attached. */
  isOverrunning: boolean;
  /** UTC ISO; omitted for online-only staff with no covering/ended shift. */
  shiftStart?: string;
  /** UTC ISO */
  shiftEnd?: string;
  shiftIds: string[];
}

const STAFF_ONLINE_ENVIRONMENT_LABELS: Record<string, string> = {
  localhost: 'Local',
  staging: 'Staging',
  ephemeral: 'Ephemeral',
  ukproduction: 'Production',
  uksandbox: 'Sandbox',
  usproduction: 'Production',
  ussandbox: 'Sandbox',
  usstaging: 'Staging',
  usephemeral: 'Ephemeral',
};

export function formatStaffOnlineEnvironmentLabel(environmentName: string): string {
  const label = STAFF_ONLINE_ENVIRONMENT_LABELS[environmentName];
  if (label) {
    return label;
  }

  const normalized = environmentName.replace(/^uk|^us/, '');
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

export function formatStaffDisplayName(name: string, staffEnvironment?: string, showEnvironmentInList = false): string {
  if (!showEnvironmentInList || !staffEnvironment) {
    return name;
  }

  return `${name} (${formatStaffOnlineEnvironmentLabel(staffEnvironment)})`;
}

export interface MergedShiftPeriod {
  staffProfileId: string;
  shiftStart: string;
  shiftEnd: string;
  shiftIds: string[];
  serviceName?: string;
}

export function toUtcIso(value: string | Date): string {
  return new Date(value).toISOString();
}

/** Wall-clock local datetime string for ListShifts (`YYYY-MM-DDTHH:mm:ss`) in `timeZone`. */
export function formatWallClockInTimeZone(date: Date, timeZone: string): string {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(date)
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, part.value])
  ) as Record<string, string>;

  return `${parts['year']}-${parts['month']}-${parts['day']}T${parts['hour']}:${parts['minute']}:${parts['second']}`;
}

export function mergeContinuingShifts(shifts: bookinginternal.GetShiftResponse[]): MergedShiftPeriod[] {
  const byStaff = new Map<string, bookinginternal.GetShiftResponse[]>();

  for (const shift of shifts) {
    if (!shift.staff_profile_id) {
      continue;
    }
    const list = byStaff.get(shift.staff_profile_id) ?? [];
    list.push(shift);
    byStaff.set(shift.staff_profile_id, list);
  }

  const merged: MergedShiftPeriod[] = [];

  for (const [staffProfileId, staffShifts] of byStaff) {
    const sorted = [...staffShifts].sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());

    let current: MergedShiftPeriod | null = null;

    for (const shift of sorted) {
      const start = toUtcIso(shift.start_date);
      const end = toUtcIso(shift.end_date);

      if (!current) {
        current = {
          staffProfileId,
          shiftStart: start,
          shiftEnd: end,
          shiftIds: [shift.id],
          serviceName: shift.service_name,
        };
        continue;
      }

      const gapMs = new Date(start).getTime() - new Date(current.shiftEnd).getTime();
      if (gapMs <= SHIFT_MERGE_GAP_MS) {
        if (new Date(end).getTime() > new Date(current.shiftEnd).getTime()) {
          current.shiftEnd = end;
        }
        current.shiftIds.push(shift.id);
      } else {
        merged.push(current);
        current = {
          staffProfileId,
          shiftStart: start,
          shiftEnd: end,
          shiftIds: [shift.id],
          serviceName: shift.service_name,
        };
      }
    }

    if (current) {
      merged.push(current);
    }
  }

  return merged;
}

function periodKey(period: MergedShiftPeriod): string {
  return `${period.staffProfileId}:${period.shiftStart}:${period.shiftIds.join(',')}`;
}

function licensedStatesFromProfile(profile?: bookinginternal.GetStaffProfileResponse): string[] {
  return (
    profile?.registered_us_state_information?.map((registration) => registration.state_information.state_shortcode) ??
    []
  );
}

export function formatLicensedStatesSubtitle(licensedStates: string[]): string {
  if (licensedStates.length > 0) {
    return `Licensed in: ${licensedStates.join(', ')}`;
  }

  return 'Not licensed';
}

export function formatProfileDescriptionSubtitle(
  profile?: bookinginternal.GetStaffProfileResponse,
  serviceName?: string,
  firestoreDescription?: string
): string {
  const rawFirestoreDescription = firestoreDescription?.trim() ?? '';
  const firestoreDescriptionValue = rawFirestoreDescription === 'unknown' ? '' : rawFirestoreDescription;
  const description = profile?.description?.trim() || firestoreDescriptionValue;
  const service = serviceName?.trim() ?? '';

  if (description && service) {
    return `${description} - ${service}`;
  }

  return description || service;
}

function toListItem(
  staffProfileId: string,
  name: string,
  photoUrl: string | undefined,
  isOnline: boolean,
  profilesById: Map<string, bookinginternal.GetStaffProfileResponse>,
  period?: MergedShiftPeriod,
  isOverrunning = false,
  staffEnvironment?: string,
  showEnvironmentInList = false,
  showStateLicensing = true,
  firestoreDescription?: string
): StaffRotaListItem {
  const profile = profilesById.get(staffProfileId);
  const resolvedName = profile?.name ?? name;
  const licensedStates = licensedStatesFromProfile(profile);

  return {
    staffProfileId,
    name: resolvedName,
    displayName: formatStaffDisplayName(resolvedName, staffEnvironment, showEnvironmentInList),
    photoUrl: profile?.photo_url ?? photoUrl,
    licensedStates,
    secondaryLine: showStateLicensing
      ? formatLicensedStatesSubtitle(licensedStates)
      : formatProfileDescriptionSubtitle(profile, period?.serviceName, firestoreDescription),
    isOnline,
    isOverrunning,
    shiftStart: period?.shiftStart,
    shiftEnd: period?.shiftEnd,
    shiftIds: period?.shiftIds ?? [],
  };
}

function latestEndedPeriod(periods: MergedShiftPeriod[], nowMs: number): MergedShiftPeriod | undefined {
  let latest: MergedShiftPeriod | undefined;

  for (const period of periods) {
    const endMs = new Date(period.shiftEnd).getTime();
    if (endMs > nowMs) {
      continue;
    }
    if (!latest || endMs > new Date(latest.shiftEnd).getTime()) {
      latest = period;
    }
  }

  return latest;
}

export function buildStaffRotaList(
  onlineStaff: StaffOnlineData[],
  shifts: bookinginternal.GetShiftResponse[],
  profiles: bookinginternal.GetStaffProfileResponse[],
  now: Date = new Date(),
  showEnvironmentInList = false,
  showStateLicensing = true
): StaffRotaListItem[] {
  const nowMs = now.getTime();
  const profilesById = new Map(profiles.map((profile) => [profile.id, profile]));
  const periods = mergeContinuingShifts(shifts);
  const periodsByStaff = new Map<string, MergedShiftPeriod[]>();

  for (const period of periods) {
    const list = periodsByStaff.get(period.staffProfileId) ?? [];
    list.push(period);
    periodsByStaff.set(period.staffProfileId, list);
  }

  const usedPeriodKeys = new Set<string>();
  const onlineRows: StaffRotaListItem[] = [];

  for (const staff of onlineStaff) {
    const staffPeriods = periodsByStaff.get(staff.id) ?? [];
    const covering = staffPeriods.find((period) => {
      const startMs = new Date(period.shiftStart).getTime();
      const endMs = new Date(period.shiftEnd).getTime();
      return startMs <= nowMs && endMs > nowMs;
    });

    if (covering) {
      usedPeriodKeys.add(periodKey(covering));
      onlineRows.push(
        toListItem(
          staff.id,
          staff.name,
          staff.photoUrl,
          true,
          profilesById,
          covering,
          false,
          staff.environment,
          showEnvironmentInList,
          showStateLicensing,
          staff.description
        )
      );
    } else {
      const ended = latestEndedPeriod(staffPeriods, nowMs);
      if (ended) {
        usedPeriodKeys.add(periodKey(ended));
        onlineRows.push(
          toListItem(
            staff.id,
            staff.name,
            staff.photoUrl,
            true,
            profilesById,
            ended,
            true,
            staff.environment,
            showEnvironmentInList,
            showStateLicensing,
            staff.description
          )
        );
      } else {
        onlineRows.push(
          toListItem(
            staff.id,
            staff.name,
            staff.photoUrl,
            true,
            profilesById,
            undefined,
            false,
            staff.environment,
            showEnvironmentInList,
            showStateLicensing,
            staff.description
          )
        );
      }
    }
  }

  const futureRows: StaffRotaListItem[] = [];

  for (const period of periods) {
    if (usedPeriodKeys.has(periodKey(period))) {
      continue;
    }
    // Drop past-ended periods for everyone; they only enrich online overrun rows above.
    if (new Date(period.shiftEnd).getTime() <= nowMs) {
      continue;
    }

    const profile = profilesById.get(period.staffProfileId);
    const online = onlineStaff.find((staff) => staff.id === period.staffProfileId);

    futureRows.push(
      toListItem(
        period.staffProfileId,
        profile?.name ?? online?.name ?? 'Unknown staff',
        profile?.photo_url ?? online?.photoUrl,
        false,
        profilesById,
        period,
        false,
        online?.environment,
        showEnvironmentInList,
        showStateLicensing
      )
    );
  }

  futureRows.sort((a, b) => new Date(a.shiftStart ?? 0).getTime() - new Date(b.shiftStart ?? 0).getTime());

  return [...onlineRows, ...futureRows];
}
